import { get } from '@classic/Foundation/Services/ApiService'
import {
  ShowNotificationAuthPopup,
  checkNotificationStatusAsync,
} from '../../Components/PushNotification/NotificationAuthPopup'
import { isReactNativeAndroid, isReactNativeApp } from '@mobi/web-native-comms/web'
import type {
  PushNotificationAlertType,
  PushNotificationRegistrationPayload,
} from '@mobi/api-types'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'

export const isPushNotificationAvailable =
  process.env.NODE_ENV === 'development' || isReactNativeApp

export async function getPushNotificationAlertStatus(
  accountNumber: number,
  alertType: PushNotificationAlertType = 'payout'
) {
  const pushAuthStatus = await checkNotificationStatusAsync()
  if (!pushAuthStatus) return

  const response = await get<{ enabled: boolean }>({
    url: `/api/credit-alerts/registrationStatus?accountNumber=${accountNumber}&fcmToken=${pushAuthStatus.fcmRegistrationToken}&alertType=${alertType}`,
    opts: { credentials: 'include' },
  })

  return response.enabled
}

export async function togglePushNotificationAlertStatus({
  accountNumber,
  isEnabled,
  alertType = 'payout',
  deepLink = '',
}: {
  accountNumber: number
  isEnabled: boolean
  alertType?: PushNotificationAlertType
  deepLink?: string
}) {
  const pushAuthStatus = await checkNotificationStatusAsync()

  if (pushAuthStatus.status !== 'Authorized' && isEnabled) {
    incrementQuickCounter(`popup-push-notif-perms-for-alerttype-${alertType}`)

    ShowNotificationAuthPopup({
      reason:
        'Notifications must be enabled on TABtouch to receive alerts. Enable notifications then try again.',
      notificationAuthStatus: pushAuthStatus.status,
    })
    return
  }

  if (!pushAuthStatus.fcmRegistrationToken) {
    return
  }

  // post JSON payload for registration
  // https://gitlab.com/rwwa/channels/notifications/tabtouch-push-alerts/
  return await postPushAlertsRegister({
    accountNumber,
    fcmToken: pushAuthStatus.fcmRegistrationToken,
    enable: isEnabled,
    deepLink,
    isAndroid: isReactNativeAndroid,
    alertType,
  })
}

export async function postPushAlertsRegister(payload: PushNotificationRegistrationPayload) {
  return await fetch('/api/credit-alerts/register', {
    method: 'post',
    credentials: 'include',
    body: JSON.stringify(payload),
  })
}

export async function handleAutoRegisterFcmToken(accountNumber: number) {
  if (!isReactNativeApp) return

  const pushAuthStatus = await checkNotificationStatusAsync()
  if (pushAuthStatus.status !== 'Authorized') return

  await new Promise(resolve => setTimeout(resolve, 3000))

  await togglePushNotificationAlertStatus({
    accountNumber,
    isEnabled: true,
    alertType: 'followme',
    deepLink: '',
  })
}
